import type { EventBuilderBlueprint, EventBuilderRequirements } from '@conference/shared';
import type { AdminEvent } from './adminEventService';
import apiClient from './apiClient';

export interface EventBuilderMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  revision: number;
  created_at: string;
}

export interface EventBuilderUpload {
  id: string;
  filename: string;
  kind: 'program' | 'registrants' | 'unsupported';
  analysis: Record<string, unknown>;
  event_id: string | null;
  created_at: string;
}

export interface EventBuilderTemplate {
  key: string;
  version: number;
  name: string;
  description: string;
  best_for: string;
  destinations: string[];
  module_keys: string[];
  quick_action_keys: string[];
}

export interface EventBuilderTemplateState {
  mode: 'discovery' | 'template' | 'custom';
  discovery_status: 'gathering' | 'ready';
  understanding: string[];
  recommended_template: {
    key: string;
    version: number;
    reason: string;
    assumptions: string[];
  } | null;
  selected_template: { key: string; version: number } | null;
  base_blueprint: Record<string, unknown>;
  overrides: Record<string, unknown>;
}

export interface EventBuilderDraftSummary {
  id: string;
  workspace_id: string;
  status: 'active' | 'converted';
  requirements: EventBuilderRequirements;
  blueprint: EventBuilderBlueprint;
  assumptions: string[];
  unsupported_requests: string[];
  catalog_version: number;
  revision: number;
  created_event_id: string | null;
  message_count: number;
  latest_assistant_summary: string | null;
  created_at: string;
  updated_at: string;
}

export interface EventBuilderDraft extends Omit<EventBuilderDraftSummary, 'message_count' | 'latest_assistant_summary'> {
  template_state: EventBuilderTemplateState;
  converted_by: string | null;
  converted_at: string | null;
  messages: EventBuilderMessage[];
  uploads: EventBuilderUpload[];
}

export const eventBuilderService = {
  listTemplates: () => apiClient.get<EventBuilderTemplate[]>('/admin/event-builder/templates'),
  list: (workspaceId: string) => apiClient.get<EventBuilderDraftSummary[]>(
    `/admin/event-builder/drafts?workspace_id=${encodeURIComponent(workspaceId)}`,
  ),
  get: (draftId: string) => apiClient.get<EventBuilderDraft>(
    `/admin/event-builder/drafts/${encodeURIComponent(draftId)}`,
  ),
  remove: (draftId: string) => apiClient.delete<void>(
    `/admin/event-builder/drafts/${encodeURIComponent(draftId)}`,
  ),
  editEvent: (eventId: string) => apiClient.post<EventBuilderDraft>(
    `/admin/events/${encodeURIComponent(eventId)}/ai-edit`,
    {},
  ),
  start: (workspaceId: string, message: string) => apiClient.post<EventBuilderDraft>(
    '/admin/event-builder/drafts',
    { workspace_id: workspaceId, message },
  ),
  sendTurn: (draftId: string, revision: number, message: string, recommendNow = false) => apiClient.post<EventBuilderDraft>(
    `/admin/event-builder/drafts/${encodeURIComponent(draftId)}/turns`,
    { revision, message, ...(recommendNow ? { recommend_now: true } : {}) },
  ),
  previewTemplate: (draftId: string, revision: number, templateKey: string) => apiClient.post<EventBuilderDraft>(
    `/admin/event-builder/drafts/${encodeURIComponent(draftId)}/template-preview`,
    { revision, template_key: templateKey },
  ),
  customize: (draftId: string, revision: number) => apiClient.post<EventBuilderDraft>(
    `/admin/event-builder/drafts/${encodeURIComponent(draftId)}/customize`,
    { revision },
  ),
  upload: (draftId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.upload<EventBuilderUpload>(`/admin/event-builder/drafts/${encodeURIComponent(draftId)}/uploads`, form);
  },
  convert: (draftId: string, revision: number) => apiClient.post<AdminEvent>(
    `/admin/event-builder/drafts/${encodeURIComponent(draftId)}/convert`,
    { revision },
  ),
};

export default eventBuilderService;
